import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { fmtInt } from '@/lib/format';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/StatusPill';
import { Topbar } from '@/components/Topbar';
import { EmptyState } from '@/components/EmptyState';
import { haptic } from '@/lib/telegram';
import { openTelegramUrl } from '@/lib/telegram-links';
import type { ChatHealth, Target, TargetDeletePreview } from '@/types/domain';

const DEFAULT_DELETE_HEALTH: ChatHealth[] = ['spam', 'dead', 'quiet', 'inaccessible'];

const DELETE_HEALTH_OPTIONS: Array<{
  health: ChatHealth;
  title: string;
  hint: string;
}> = [
  { health: 'spam', title: 'Спам', hint: 'Высокая доля спам-сообщений.' },
  { health: 'dead', title: 'Мёртвые', hint: 'Почти нет движения и слабая активность.' },
  { health: 'quiet', title: 'Тихие', hint: 'Есть разговор, но объём слабый и нерегулярный.' },
  { health: 'inaccessible', title: 'Нет доступа', hint: 'Чат недоступен или ссылка уже не работает.' },
  { health: 'unknown', title: 'Не оценённые', hint: 'Здоровье ещё ни разу не проверялось.' },
  { health: 'pending', title: 'Оценка идёт', hint: 'Проверка ещё не завершилась.' },
  { health: 'alive', title: 'Живые', hint: 'Нормальные активные чаты.' },
];

export function CampaignChatsPage(): JSX.Element {
  const { id } = useParams();
  const cid = Number(id);
  const { data: targets, loading, reload } = useAsync(() => api.listTargets(cid), [cid]);
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [evaluateOpen, setEvaluateOpen] = useState(false);
  const [evaluateBusy, setEvaluateBusy] = useState(false);
  const [evaluateError, setEvaluateError] = useState<string | null>(null);
  const [audienceDescription, setAudienceDescription] = useState('');
  const [lastAudienceDescription, setLastAudienceDescription] = useState('');
  const [evaluateNotice, setEvaluateNotice] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState<'filters' | 'preview'>('filters');
  const [deleteHealth, setDeleteHealth] = useState<ChatHealth[]>(DEFAULT_DELETE_HEALTH);
  const [deletePreview, setDeletePreview] = useState<TargetDeletePreview | null>(null);
  const [excludedTargetIds, setExcludedTargetIds] = useState<number[]>([]);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const sortedTargets = useMemo(() => {
    const list = [...(targets ?? [])];
    return list.sort((left, right) => compareTargets(right, left));
  }, [targets]);

  const stats = useMemo(() => {
    const list = sortedTargets;
    return {
      total: list.length,
      healthy: list.filter((target) => target.health === 'alive' || target.health === 'quiet').length,
      blocked: list.filter((target) => target.health === 'inaccessible').length,
    };
  }, [sortedTargets]);

  const selectedDeleteTargets = useMemo(
    () => (deletePreview?.items ?? []).filter((target) => !excludedTargetIds.includes(target.id)),
    [deletePreview, excludedTargetIds],
  );

  const closeDeleteFlow = () => {
    setDeleteOpen(false);
    setDeleteStep('filters');
    setDeleteHealth(DEFAULT_DELETE_HEALTH);
    setDeletePreview(null);
    setExcludedTargetIds([]);
    setDeleteBusy(false);
    setDeleteError(null);
    setConfirmDeleteOpen(false);
  };

  const openDeleteFlow = () => {
    setDeleteOpen(true);
    setDeleteStep('filters');
    setDeleteHealth(DEFAULT_DELETE_HEALTH);
    setDeletePreview(null);
    setExcludedTargetIds([]);
    setDeleteBusy(false);
    setDeleteError(null);
    setConfirmDeleteOpen(false);
  };

  const submit = async () => {
    setBusy(true);
    haptic('medium');
    const lines = raw.split('\n').map((value) => value.trim()).filter(Boolean);
    await api.addTargets(cid, lines);
    setBusy(false);
    setOpen(false);
    setRaw('');
    reload();
  };

  const openEvaluateFlow = () => {
    setAudienceDescription(lastAudienceDescription);
    setEvaluateError(null);
    setEvaluateOpen(true);
  };

  const closeEvaluateFlow = () => {
    setEvaluateOpen(false);
    setEvaluateBusy(false);
    setEvaluateError(null);
  };

  const evaluateAll = async () => {
    setEvaluateBusy(true);
    setEvaluateError(null);
    haptic('medium');
    try {
      const taSignals = audienceDescription
        .split(/[;,\n]+/g)
        .map((item) => item.trim())
        .filter((item) => item.length >= 3)
        .slice(0, 20);
      const response = await api.evaluateAllTargets(cid, {
        audienceDescription: audienceDescription.trim(),
        taSignals,
      });
      setLastAudienceDescription(response.audienceDescription ?? audienceDescription.trim());
      setEvaluateNotice('Переоценка запущена. Чаты обновятся в течение 2-10 минут.');
      closeEvaluateFlow();
    } catch (error) {
      setEvaluateError(String((error as Error)?.message ?? error));
    } finally {
      setEvaluateBusy(false);
    }
  };

  const openTargetChat = (target: Target) => {
    const link = buildTelegramChatUrl(target.normalizedLink);
    if (!link) return;
    haptic('light');
    openTelegramUrl(link);
  };

  const toggleDeleteHealth = (health: ChatHealth) => {
    setDeleteHealth((current) =>
      current.includes(health)
        ? current.filter((item) => item !== health)
        : [...current, health],
    );
  };

  const loadDeletePreview = async () => {
    if (deleteHealth.length === 0) return;
    setDeleteBusy(true);
    setDeleteError(null);
    haptic('medium');
    try {
      const preview = await api.previewTargetDelete(cid, deleteHealth);
      setDeletePreview(preview);
      setExcludedTargetIds([]);
      setDeleteStep('preview');
    } catch (error) {
      setDeleteError(String((error as Error)?.message ?? error));
    } finally {
      setDeleteBusy(false);
    }
  };

  const toggleDeleteTarget = (targetId: number) => {
    setExcludedTargetIds((current) =>
      current.includes(targetId)
        ? current.filter((item) => item !== targetId)
        : [...current, targetId],
    );
  };

  const executeDelete = async () => {
    if (selectedDeleteTargets.length === 0) return;
    setDeleteBusy(true);
    setDeleteError(null);
    haptic('medium');
    try {
      await api.bulkDeleteTargets(
        cid,
        selectedDeleteTargets.map((target) => target.id),
      );
      closeDeleteFlow();
      reload();
    } catch (error) {
      setDeleteError(String((error as Error)?.message ?? error));
    } finally {
      setDeleteBusy(false);
      setConfirmDeleteOpen(false);
    }
  };

  return (
    <div className="space-y-4 pb-8">
      <Topbar
        title="Чаты кампании"
        subtitle={`${stats.total} чатов · ${stats.healthy} живых · ${stats.blocked} без доступа`}
        right={
          <button
            onClick={() => setOpen(true)}
            className="flex h-12 w-12 items-center justify-center rounded-[20px] bg-[#6077ff] text-white shadow-[0_16px_28px_rgba(78,101,255,0.34)] active:scale-95"
          >
            <Icon name="plus" />
          </button>
        }
      />

      <button onClick={openEvaluateFlow} className="soft-outline-button">
        <Icon name="shield" size={18} />
        Оценить здоровье всех чатов
      </button>

      <button onClick={openDeleteFlow} className="btn-danger w-full">
        <Icon name="x" size={16} />
        Удалить чаты по статусам
      </button>

      {loading && (
        <div className="space-y-3">
          <div className="skeleton h-28" />
          <div className="skeleton h-28" />
          <div className="skeleton h-28" />
        </div>
      )}

      {!loading && (targets ?? []).length === 0 && (
        <EmptyState
          icon="shield"
          title="Чаты ещё не добавлены"
          body="Вставьте список ссылок или @username по одной строке. Дальше их можно массово переоценивать."
          cta={<button className="btn-primary" onClick={() => setOpen(true)}>Добавить чаты</button>}
        />
      )}

      <ul className="space-y-3">
        {sortedTargets.map((target) => (
          <li key={target.id} className="card p-0">
            <div className="flex items-start gap-3 p-4">
              <button
                type="button"
                onClick={() => openTargetChat(target)}
                className="flex min-w-0 flex-1 items-start gap-3 transition active:scale-[0.992]"
              >
                <span className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] border border-[#1c2449] bg-[#0f1430] text-[#9bafff]">
                  <Icon name="message" size={19} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-[16px] font-semibold text-white">
                        {target.title || target.normalizedLink}
                      </div>
                      <div className="mt-1 truncate text-[13px] text-[#7f87aa]">
                        {target.normalizedLink}
                        {target.messagesPerDay ? ` · ${fmtInt(target.messagesPerDay)} сообщ/день` : ''}
                      </div>
                      {target.healthReason && (
                        <div className="mt-2 line-clamp-2 text-[12px] leading-5 text-[#a8b0d4]">
                          {target.healthReason}
                        </div>
                      )}
                    </div>
                    <StatusPill status={target.health} withDot={false} />
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <Bar value={target.healthScore ?? 0} label="health" />
                    <Bar value={target.targetAudienceScore ?? 0} label="ЦА" />
                  </div>
                </div>
              </button>

              <span className="rounded-xl p-2 text-[#70789d]">
                <Icon name="chevron-right" />
              </span>
            </div>
          </li>
        ))}
      </ul>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/65 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-t-[32px] border border-white/10 bg-[#0c1020] p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-white/10" />
            <div className="text-[17px] font-semibold text-white">Добавить чаты</div>
            <div className="mt-1 text-[13px] text-[#7d85a9]">По одной ссылке или @username на строку.</div>
            <textarea
              className="textarea mt-4 w-full rounded-[22px] border border-white/10 bg-white/4 px-4 py-4 text-[14px] text-white outline-none"
              placeholder={'https://t.me/cryptochat\nhttps://t.me/joinchat/AbCdEf\n@some_chat'}
              value={raw}
              onChange={(event) => setRaw(event.target.value)}
            />
            <div className="mt-4 flex gap-2">
              <button className="btn-ghost flex-1" onClick={() => setOpen(false)}>Отмена</button>
              <button className="btn-primary flex-1" disabled={!raw.trim() || busy} onClick={submit}>
                {busy ? 'Добавляю…' : 'Добавить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {evaluateOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/65 backdrop-blur-sm"
          onClick={closeEvaluateFlow}
        >
          <div
            className="w-full max-w-md rounded-t-[32px] border border-white/10 bg-[#0c1020] p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-white/10" />
            <div className="text-[17px] font-semibold text-white">Переоценить чаты</div>
            <div className="mt-1 text-[13px] text-[#7d85a9]">
              Кратко опишите, кто ваша ЦА. Этот контекст будет использован для нового прогона по всем чатам.
            </div>
            <textarea
              className="textarea mt-4 w-full rounded-[22px] border border-white/10 bg-white/4 px-4 py-4 text-[14px] text-white outline-none"
              placeholder="Например: продавцы Wildberries, менеджеры маркетплейсов, агентства, люди с опытом работы с юнит-экономикой"
              value={audienceDescription}
              onChange={(event) => setAudienceDescription(event.target.value)}
            />
            {lastAudienceDescription && !audienceDescription.trim() && (
              <div className="mt-3 text-[12px] text-[#8f98bc]">
                Последний контекст: {lastAudienceDescription}
              </div>
            )}
            {evaluateError && (
              <div className="mt-4 rounded-[20px] border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-200">
                {evaluateError}
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button className="btn-ghost flex-1" disabled={evaluateBusy} onClick={closeEvaluateFlow}>
                Отмена
              </button>
              <button className="btn-primary flex-1" disabled={evaluateBusy} onClick={evaluateAll}>
                {evaluateBusy ? 'Оцениваю…' : 'Запустить заново'}
              </button>
            </div>
          </div>
        </div>
      )}

      {evaluateNotice && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
          onClick={() => setEvaluateNotice(null)}
        >
          <div
            className="w-full max-w-md rounded-t-[32px] border border-white/10 bg-[#0c1020] p-5 sm:rounded-[28px]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-white/10 sm:hidden" />
            <div className="text-[17px] font-semibold text-white">Переоценка запущена</div>
            <div className="mt-2 text-[13px] leading-6 text-[#a7b0d3]">
              {evaluateNotice}
            </div>
            <div className="mt-4 flex gap-2">
              <button className="btn-primary flex-1" onClick={() => setEvaluateNotice(null)}>
                Понял
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/65 backdrop-blur-sm"
          onClick={closeDeleteFlow}
        >
          <div
            className="w-full max-w-md rounded-t-[32px] border border-white/10 bg-[#0c1020] p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-white/10" />

            {deleteStep === 'filters' && (
              <>
                <div className="text-[17px] font-semibold text-white">Удаление по статусам</div>
                <div className="mt-1 text-[13px] text-[#7d85a9]">
                  Выберите health-статусы. На следующем шаге можно снять галку с чатов, которые всё-таки надо оставить.
                </div>

                <div className="mt-4 space-y-2">
                  {DELETE_HEALTH_OPTIONS.map((option) => {
                    const active = deleteHealth.includes(option.health);
                    return (
                      <button
                        key={option.health}
                        type="button"
                        onClick={() => toggleDeleteHealth(option.health)}
                        className={`w-full rounded-[22px] border px-4 py-3 text-left transition ${
                          active
                            ? 'border-rose-400/40 bg-rose-500/10'
                            : 'border-white/10 bg-white/[0.03]'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="space-y-1">
                            <StatusPill status={option.health} withDot={false} />
                            <div className="text-[12px] text-[#7d85a9]">{option.hint}</div>
                          </div>
                          <span
                            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                              active
                                ? 'border-rose-300/60 bg-rose-500/20 text-rose-200'
                                : 'border-white/10 text-transparent'
                            }`}
                          >
                            <Icon name="check" size={14} />
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {deleteError && (
                  <div className="mt-4 rounded-[20px] border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-200">
                    {deleteError}
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  <button className="btn-ghost flex-1" onClick={closeDeleteFlow}>
                    Отмена
                  </button>
                  <button
                    className="btn-danger flex-1"
                    disabled={deleteHealth.length === 0 || deleteBusy}
                    onClick={loadDeletePreview}
                  >
                    {deleteBusy ? 'Собираю…' : 'Далее'}
                  </button>
                </div>
              </>
            )}

            {deleteStep === 'preview' && deletePreview && (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[17px] font-semibold text-white">Кандидаты на удаление</div>
                    <div className="mt-1 text-[13px] text-[#7d85a9]">
                      Под фильтры попало {fmtInt(deletePreview.summary.matched)} чатов. Снимите галку с тех, которые хотите оставить.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDeleteStep('filters')}
                    className="rounded-full border border-white/10 p-2 text-[#9ca7d4]"
                  >
                    <Icon name="chevron-left" size={16} />
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {deletePreview.summary.byHealth
                    .filter((item) => item.count > 0)
                    .map((item) => (
                      <div
                        key={item.health}
                        className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[12px] text-[#c5ccef]"
                      >
                        {statusLabel(item.health)} · {fmtInt(item.count)}
                      </div>
                    ))}
                </div>

                {deletePreview.items.length === 0 ? (
                  <div className="mt-4 rounded-[24px] border border-white/10 bg-white/[0.03] p-4 text-[13px] text-[#a7b0d3]">
                    Под выбранные статусы сейчас ничего не попало.
                  </div>
                ) : (
                  <div className="mt-4 max-h-[48vh] space-y-2 overflow-y-auto pr-1">
                    {deletePreview.items.map((target) => {
                      const selected = !excludedTargetIds.includes(target.id);
                      return (
                        <button
                          key={target.id}
                          type="button"
                          onClick={() => toggleDeleteTarget(target.id)}
                          className={`w-full rounded-[22px] border px-4 py-3 text-left transition ${
                            selected
                              ? 'border-rose-400/40 bg-rose-500/10'
                              : 'border-white/10 bg-white/[0.03] opacity-75'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-[15px] font-semibold text-white">
                                {target.title || target.normalizedLink}
                              </div>
                              <div className="mt-1 truncate text-[12px] text-[#7d85a9]">
                                {target.normalizedLink}
                                {target.messagesPerDay
                                  ? ` · ${fmtInt(target.messagesPerDay)} сообщ/день`
                                  : ''}
                              </div>
                              <div className="mt-2">
                                <StatusPill status={target.health} withDot={false} />
                              </div>
                              {target.healthReason && (
                                <div className="mt-2 line-clamp-2 text-[12px] leading-5 text-[#a8b0d4]">
                                  {target.healthReason}
                                </div>
                              )}
                            </div>
                            <span
                              className={`mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                                selected
                                  ? 'border-rose-300/60 bg-rose-500/20 text-rose-200'
                                  : 'border-white/10 text-transparent'
                              }`}
                            >
                              <Icon name="check" size={14} />
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {deleteError && (
                  <div className="mt-4 rounded-[20px] border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-200">
                    {deleteError}
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  <button className="btn-ghost flex-1" onClick={() => setDeleteStep('filters')}>
                    Назад
                  </button>
                  <button
                    className="btn-danger flex-1"
                    disabled={selectedDeleteTargets.length === 0 || deleteBusy}
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    Удалить {fmtInt(selectedDeleteTargets.length)}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {confirmDeleteOpen && deletePreview && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
          onClick={() => setConfirmDeleteOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-t-[32px] border border-white/10 bg-[#0c1020] p-5 sm:rounded-[28px]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-white/10 sm:hidden" />
            <div className="text-[17px] font-semibold text-white">
              Удалить {fmtInt(selectedDeleteTargets.length)} чатов?
            </div>
            <div className="mt-2 text-[13px] text-[#7d85a9]">
              Из кампании исчезнут сами чаты и связанные оценки, назначения на вступление и истории посева для выбранных записей.
            </div>
            <div className="mt-4 flex gap-2">
              <button
                className="btn-ghost flex-1"
                disabled={deleteBusy}
                onClick={() => setConfirmDeleteOpen(false)}
              >
                Отмена
              </button>
              <button
                className="btn-danger flex-1"
                disabled={deleteBusy}
                onClick={executeDelete}
              >
                {deleteBusy ? 'Удаляю…' : 'Подтвердить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function statusLabel(status: ChatHealth): string {
  switch (status) {
    case 'spam':
      return 'Спам';
    case 'dead':
      return 'Мёртвые';
    case 'quiet':
      return 'Тихие';
    case 'inaccessible':
      return 'Нет доступа';
    case 'unknown':
      return 'Не оценённые';
    case 'pending':
      return 'Оценка';
    case 'alive':
      return 'Живые';
  }
}

function buildTelegramChatUrl(normalizedLink: string | undefined): string {
  if (!normalizedLink) return '';
  if (normalizedLink.startsWith('@')) return `https://t.me/${normalizedLink.slice(1)}`;
  if (/^https?:\/\//.test(normalizedLink)) return normalizedLink;
  return `https://t.me/${normalizedLink.replace(/^\/+/, '')}`;
}

function healthRank(health: ChatHealth): number {
  switch (health) {
    case 'alive':
      return 6;
    case 'quiet':
      return 5;
    case 'unknown':
      return 4;
    case 'pending':
      return 3;
    case 'dead':
      return 2;
    case 'inaccessible':
      return 1;
    case 'spam':
      return 0;
  }
}

function compareTargets(left: Target, right: Target): number {
  const healthDelta = healthRank(left.health) - healthRank(right.health);
  if (healthDelta !== 0) return healthDelta;
  const taDelta = (left.targetAudienceScore ?? -1) - (right.targetAudienceScore ?? -1);
  if (taDelta !== 0) return taDelta;
  const scoreDelta = (left.healthScore ?? -1) - (right.healthScore ?? -1);
  if (scoreDelta !== 0) return scoreDelta;
  return (left.membersCount ?? 0) - (right.membersCount ?? 0);
}

function Bar({ value, label }: { value: number; label: string }): JSX.Element {
  const clamped = Math.max(0, Math.min(1, value));

  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-[12px] text-[#8189ac]">
        <span>{label}</span>
        <span>{Math.round(clamped * 100)}%</span>
      </div>
      <div className="h-[7px] overflow-hidden rounded-full bg-[#171c35]">
        <div
          className="h-full rounded-full bg-[linear-gradient(90deg,#4054ff_0%,#6e89ff_100%)]"
          style={{ width: `${clamped * 100}%` }}
        />
      </div>
    </div>
  );
}
